/**
 * @file ForgotPassword.js — Page de demande de réinitialisation de mot de passe.
 * L'utilisateur saisit son adresse e-mail et reçoit un lien de réinitialisation
 * par e-mail si le compte existe (R-0042).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import api from '../lib/api';
import { APP_ROUTES } from '../lib/routes';

/**
 * Formulaire de demande de réinitialisation de mot de passe.
 * Affiche un message de confirmation après l'envoi, quel que soit l'existence
 * du compte (anti-énumération d'e-mails).
 * @returns {React.ReactElement}
 */
export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  /** Soumet la demande de réinitialisation à l'API. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/password-reset/', { email });
    } catch {
      /* Le backend retourne toujours 200 (anti-énumération). */
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-sm px-6">
        {sent ? (
          /* État de confirmation — affiché après l'envoi */
          <div className="text-center">
            <MailCheck className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('forgot_password.check_email_title')}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {t('forgot_password.check_email_details', { email })}
            </p>
            <Button asChild variant="outline" className="rounded-sm">
              <Link to={APP_ROUTES.login}><ArrowLeft className="h-4 w-4 mr-2" /> {t('forgot_password.back_to_sign_in')}</Link>
            </Button>
          </div>
        ) : (
          /* Formulaire de saisie de l'e-mail */
          <>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('forgot_password.title')}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              {t('forgot_password.subtitle')}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('login.email')}</Label>
                <Input
                  id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring"
                  required data-testid="forgot-email-input" autoComplete="email"
                />
              </div>
              <Button type="submit" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('forgot_password.send_reset_link')}
              </Button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground text-center">
              <Link to={APP_ROUTES.login} className="text-foreground font-medium hover:underline">{t('forgot_password.back_to_sign_in')}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
